import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuizGame } from "../../hooks/useLiveQuizGame";
import { useTranslation } from "../../hooks/useTranslation";

const OPTIONS = [
  { bg: "#0a2a6e", icon: "▲" },
  { bg: "#1368ce", icon: "◆" },
  { bg: "#2fa8e0", icon: "●" },
  { bg: "#0d7c8f", icon: "■" },
];

export default function LiveQuizStudentGame() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    phase,
    question,
    timeLeft,
    voteCounts,
    correctIndex,
    myScore,
    leaderboard,
    hasAnswered,
    submitAnswer,
  } = useLiveQuizGame(roomCode);

  if (phase === "lobby") return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: "#1a1a2e" }}>
      <div className="text-center text-white">
        <div className="spinner-border mb-3" />
        <p>{t.liveQuiz.waitingForTeacher}</p>
      </div>
    </div>
  );

  if (phase === "teacher_left") return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: "#1a1a2e" }}>
      <div className="text-center text-white">
        <p className="fs-4">{t.liveQuiz.teacherEnded}</p>
        <button className="btn btn-outline-light mt-3" onClick={() => navigate("/")}>
          {t.common.back}
        </button>
      </div>
    </div>
  );

  if (phase === "question") return (
    <div className="min-vh-100 d-flex flex-column" style={{ background: "#1a1a2e" }}>
      <div className="p-3">
        <div className="progress" style={{ height: 8 }}>
          <div
            className="progress-bar bg-warning"
            style={{
              width: `${(timeLeft / question.timeLimit) * 100}%`,
              transition: "width 1s linear",
            }}
          />
        </div>
        <div className="d-flex justify-content-between text-white opacity-75 mt-1">
          <small>{t.liveQuiz.questionOf.replace("{{current}}", question.index + 1).replace("{{total}}", question.total)}</small>
          <small>⏱ {timeLeft}s</small>
        </div>
      </div>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center p-4">
        <h3 className="text-white text-center fw-bold">{question.text}</h3>
      </div>

      <div className="p-4 pb-5">
        <div className="row g-3">
          {question.options.map((opt, i) => (
            <div className="col-6" key={i}>
              <button
                className="btn w-100 py-3 fw-bold text-white"
                style={{
                  background: OPTIONS[i].bg,
                  opacity: hasAnswered ? 0.5 : 1,
                  border: "none",
                  borderRadius: 12,
                  minHeight: 80,
                  fontSize: "1rem",
                }}
                onClick={() => submitAnswer(i)}
                disabled={hasAnswered}
              >
                {OPTIONS[i].icon} {opt}
              </button>
            </div>
          ))}
        </div>
        {hasAnswered && (
          <p className="text-center text-white opacity-75 mt-3">
            {t.liveQuiz.answerSubmitted}
          </p>
        )}
      </div>
    </div>
  );

  if (phase === "result") return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: myScore.isCorrect ? "#0d6e0d" : "#8b0000" }}>
      <div className="display-1 mb-3">{myScore.isCorrect ? "✅" : "❌"}</div>
      <h2 className="text-white fw-bold mb-2">
        {myScore.isCorrect
          ? t.liveQuiz.pointsEarned.replace("{{delta}}", myScore.delta)
          : t.liveQuiz.wrong}
      </h2>
      <p className="text-white opacity-75">
        {t.liveQuiz.total}: {myScore.total} {t.liveQuiz.points}
      </p>

      <div className="w-100 mt-4" style={{ maxWidth: 400 }}>
        {voteCounts.map((count, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <span style={{ width: 24, color: OPTIONS[i].bg }}>
              {OPTIONS[i].icon}
            </span>
            <div className="progress flex-grow-1" style={{ height: 28 }}>
              <div
                className="progress-bar text-start ps-2 fw-bold"
                style={{
                  width: `${Math.max((count / Math.max(...voteCounts, 1)) * 100, 5)}%`,
                  background: i === correctIndex ? "#26890c" : OPTIONS[i].bg,
                  transition: "width 0.6s ease",
                }}
              >
                {count}
              </div>
            </div>
            {i === correctIndex && <span className="text-white fw-bold">✓</span>}
          </div>
        ))}
      </div>
    </div>
  );

  if (phase === "leaderboard") return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: "#1a1a2e" }}>
      <h3 className="text-white fw-bold mb-4">🏆 {t.liveQuiz.leaderboard}</h3>
      {leaderboard.map((p, i) => (
        <div key={i}
          className="d-flex justify-content-between align-items-center bg-white bg-opacity-10 rounded-3 px-4 py-3 mb-2 w-100 text-white"
          style={{ maxWidth: 420 }}>
          <span className="fw-bold">
            {["🥇", "🥈", "🥉"][i] ?? `#${p.rank}`} {p.name}
          </span>
          <span>
            {p.score}{" "}
            {p.lastDelta > 0 && <small className="text-success">+{p.lastDelta}</small>}
          </span>
        </div>
      ))}
    </div>
  );

  if (phase === "finished") return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4"
      style={{ background: "#1a1a2e" }}>
      <h2 className="text-white fw-bold mb-1">🎉 {t.liveQuiz.quizFinished}</h2>
      <p className="text-white opacity-75 mb-4">{t.liveQuiz.finalResult}</p>
      {leaderboard.map((p, i) => (
        <div key={i}
          className="d-flex justify-content-between align-items-center bg-white bg-opacity-10 rounded-3 px-4 py-3 mb-2 w-100 text-white"
          style={{ maxWidth: 420 }}>
          <span className="fw-bold fs-5">
            {["🥇", "🥈", "🥉"][i] ?? `#${p.rank}`} {p.name}
          </span>
          <span className="fs-5">{p.score} {t.liveQuiz.points}</span>
        </div>
      ))}
      <button className="btn btn-outline-light mt-4" onClick={() => navigate("/")}>
        {t.liveQuiz.backToHome}
      </button>
    </div>
  );

  return null;
}
